/**
 * SocialFeed Component
 * Fetches the most recent profile data for each post author
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Heart, 
  Send, 
  MoreVertical,
  Clock,
  Video,
  Image as ImageIcon,
  User,
  ChevronDown,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import { collection, addDoc, updateDoc, arrayUnion, arrayRemove, doc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { getUserProfile } from '../../../services/firebase/profileService';
import { toast } from 'react-hot-toast';

/**
 * SocialFeed Component - Fetches current profile data
 */
const SocialFeed = ({ 
  posts, 
  loading, 
  error, 
  formatTimestamp, 
  fetchMorePosts, 
  hasMore, 
  onRefresh 
}) => {
  const { currentUser, userProfile } = useAuth();
  const [expandedPost, setExpandedPost] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [expandedMedia, setExpandedMedia] = useState(null);
  const [authorProfiles, setAuthorProfiles] = useState({});
  const [loadingProfiles, setLoadingProfiles] = useState(new Set());
  const observer = useRef();
  
  // Function to handle the intersection observer for infinite scrolling
  const lastPostElementRef = (node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMorePosts();
      }
    });
    
    if (node) observer.current.observe(node);
  };

  // Fetch author profile data
  const fetchAuthorProfile = async (authorId) => {
    if (authorProfiles[authorId] || loadingProfiles.has(authorId)) {
      return; // Already have the profile or currently loading
    }

    setLoadingProfiles(prev => new Set(prev).add(authorId));

    try {
      const profile = await getUserProfile(authorId);
      if (profile) {
        setAuthorProfiles(prev => ({
          ...prev,
          [authorId]: {
            avatar: profile.photos?.[0] || '',
            name: profile.basicInfo?.name || 'Anonymous',
            username: profile.basicInfo?.username || '',
            university: profile.academic?.university || '',
            lastUpdated: Date.now()
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching author profile:', error);
    } finally {
      setLoadingProfiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(authorId);
        return newSet;
      });
    }
  };

  // Get author display data (use fetched profile if available, fallback to stored data)
  const getAuthorDisplayData = (post) => {
    const currentProfile = authorProfiles[post.authorId];
    const storedAuthor = post.author;

    if (currentProfile) {
      return currentProfile;
    }

    // Trigger profile fetch if we don't have it
    if (!loadingProfiles.has(post.authorId)) {
      fetchAuthorProfile(post.authorId);
    }

    // Return stored data as fallback
    return {
      avatar: storedAuthor?.avatar || '',
      name: storedAuthor?.name || 'Anonymous',
      username: storedAuthor?.username || '',
      university: storedAuthor?.university || ''
    };
  };

  // Fetch profiles for all visible posts
  useEffect(() => {
    const uniqueAuthorIds = [...new Set(posts.map(post => post.authorId))];
    uniqueAuthorIds.forEach(authorId => {
      if (!authorProfiles[authorId] && !loadingProfiles.has(authorId)) {
        fetchAuthorProfile(authorId);
      }
    });
  }, [posts]);

  // Clear old profile data periodically (optional optimization)
  useEffect(() => {
    const interval = setInterval(() => {
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      setAuthorProfiles(prev => {
        const filtered = {};
        Object.keys(prev).forEach(authorId => {
          if (prev[authorId].lastUpdated > fiveMinutesAgo) {
            filtered[authorId] = prev[authorId];
          }
        });
        return filtered;
      });
    }, 5 * 60 * 1000); // Run every 5 minutes

    return () => clearInterval(interval);
  }, []);

  // Toggle a post to show/hide comments
  const togglePostExpansion = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
    setCommentText('');
  };

  // Handle like/unlike a post
  const handleLikePost = async (post) => {
    if (!currentUser) return;
    
    const postRef = doc(db, 'posts', post.id);
    const userId = currentUser.uid;
    const isLiked = post.likes && post.likes.includes(userId);
    
    try {
      await updateDoc(postRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
      
      // Show success toast for better UX
      if (!isLiked) {
        toast.success('Post liked!', { duration: 1500 });
      }
    } catch (error) {
      console.error('Error updating like:', error);
      toast.error('Could not update like');
    }
  };

  // Submit a new comment
  const handleSubmitComment = async (postId) => {
    if (!commentText.trim() || !currentUser || submittingComment) return;
    
    setSubmittingComment(true);
    
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      
      const commentData = {
        author: {
          avatar: userProfile?.photos?.[0] || '',
          name: userProfile?.basicInfo?.name || 'Anonymous',
          username: userProfile?.username || userProfile?.basicInfo?.username || 'user',
        },
        authorId: currentUser.uid,
        content: commentText.trim(),
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0,
        parentCommentId: null,
        postId: postId,
        replies: []
      };
      
      await addDoc(commentsRef, commentData);
      
      // Update post's comment count
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        commentsCount: increment(1)
      });
      
      setCommentText('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Could not add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Toggle expanded media view
  const toggleExpandMedia = (post) => {
    if (expandedMedia === post.id) {
      setExpandedMedia(null);
    } else {
      setExpandedMedia(post.id);
    }
  };

  // Determine media type icon
  const getMediaTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video size={16} className="mr-1" />;
      case 'image':
        return <ImageIcon size={16} className="mr-1" />;
      default:
        return null;
    }
  };

  // Calculate how long ago a post was created
  const getTimeAgo = (createdAt) => {
    if (!createdAt) return 'Recently';
    return formatTimestamp(createdAt);
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm">
        {error}
        <button onClick={onRefresh} className="ml-2 underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.length === 0 && !loading ? (
        <div className="p-6 text-center text-gray-500 bg-white rounded-lg shadow-sm border border-gray-200">
          <p>No posts yet. Be the first to share something!</p>
        </div>
      ) : (
        posts.map((post, index) => {
          // For the last post, add a ref for infinite scrolling
          const isLastPost = index === posts.length - 1;
          const isExpanded = expandedMedia === post.id;
          
          // Get current author display data
          const authorData = getAuthorDisplayData(post);
          
          return (
            <div 
              key={post.id}
              ref={isLastPost ? lastPostElementRef : null}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
            >
              {/* Post Header */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center">
                  <div className="w-9 h-9 rounded-full overflow-hidden mr-2.5 relative">
                    {authorData.avatar ? (
                      <>
                        <img 
                          src={authorData.avatar} 
                          alt={authorData.name || 'Anonymous'} 
                          className="w-full h-full object-cover"
                        />
                        {/* Loading indicator for profile update */}
                        {loadingProfiles.has(post.authorId) && (
                          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                            <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 rounded-full">
                        {authorData.name?.charAt(0) || <User size={20} />}
                        {loadingProfiles.has(post.authorId) && (
                          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                            <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      {authorData.name || 'Anonymous'}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <span className="flex items-center">
                        <Clock size={12} className="mr-1" />
                        {getTimeAgo(post.createdAt)}
                      </span>
                      {authorData.username && (
                        <>
                          <span className="mx-1">•</span>
                          <span>@{authorData.username}</span>
                        </>
                      )}
                      {authorData.university && (
                        <>
                          <span className="mx-1">•</span>
                          <span className="text-blue-600">{authorData.university}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <MoreVertical size={18} />
                </button>
              </div>

              {/* Post Content */}
              <div className="px-3 py-2">
                <p className="text-gray-800 text-sm whitespace-pre-line">
                  {post.content}
                </p>
              </div>

              {/* Post Media - With Optimized Size */}
              {post.media && (
                <div 
                  className={`relative cursor-pointer ${isExpanded ? '' : 'max-h-56 overflow-hidden'}`}
                  onClick={() => toggleExpandMedia(post)}
                >
                  {post.media.type === 'image' ? (
                    <img 
                      src={post.media.url} 
                      alt="Post attachment" 
                      className={`w-full h-auto ${isExpanded ? '' : 'max-h-56 object-cover'}`}
                      loading="lazy"
                    />
                  ) : post.media.type === 'video' ? (
                    <video 
                      src={post.media.url} 
                      controls 
                      className={`w-full h-auto ${isExpanded ? '' : 'max-h-56'}`}
                      preload="metadata"
                    />
                  ) : null}
                  
                  <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full flex items-center z-10">
                    {getMediaTypeIcon(post.media.type)}
                    {post.media.type.charAt(0).toUpperCase() + post.media.type.slice(1)}
                  </div>
                  
                  {!isExpanded && (
                    <div className="absolute bottom-0 inset-x-0 h-8 bg-gradient-to-t from-black/30 to-transparent flex items-center justify-center">
                      <ChevronDown size={20} className="text-white" />
                    </div>
                  )}
                  
                  {isExpanded && (
                    <button 
                      className="absolute top-2 right-12 bg-black bg-opacity-60 text-white p-1 rounded-full z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMedia(null);
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )}

              {/* Post Footer */}
              <div className="px-3 py-2.5 border-t border-gray-100 flex justify-between">
                <button 
                  className={`flex items-center ${
                    post.likes && currentUser && post.likes.includes(currentUser.uid) 
                      ? 'text-red-500' 
                      : 'text-gray-500 hover:text-red-500'
                  }`}
                  onClick={() => handleLikePost(post)}
                >
                  <Heart 
                    size={18} 
                    className="mr-1" 
                    fill={post.likes && currentUser && post.likes.includes(currentUser.uid) ? "currentColor" : "none"} 
                  />
                  <span className="text-xs">{post.likesCount || 0}</span>
                </button>
                <button 
                  className="flex items-center text-gray-500 hover:text-blue-500"
                  onClick={() => togglePostExpansion(post.id)}
                >
                  <MessageSquare size={18} className="mr-1" />
                  <span className="text-xs">{post.commentsCount || 0}</span>
                </button>
              </div>

              {/* Comments Section (expandable) */}
              {expandedPost === post.id && (
                <div className="bg-gray-50 p-3 border-t border-gray-200">
                  {/* Comment Input */}
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                      {userProfile?.photos?.[0] ? (
                        <img 
                          src={userProfile.photos[0]} 
                          alt="Your avatar" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500 rounded-full">
                          {userProfile?.basicInfo?.name?.charAt(0) || <User size={16} />}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        className="w-full py-2 px-3 bg-white border border-gray-300 rounded-full text-sm"
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment(post.id)}
                      />
                      <button 
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-500 disabled:text-gray-300"
                        disabled={!commentText.trim() || submittingComment}
                        onClick={() => handleSubmitComment(post.id)}
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Comments will be loaded/displayed here in a real implementation */}
                  <div className="text-center py-2 text-sm text-gray-500">
                    Loading comments...
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Loading indicator for more posts */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
        </div>
      )}

      {/* End of feed message */}
      {!hasMore && posts.length > 0 && !loading && (
        <div className="text-center py-3 text-gray-500 text-sm">
          You've reached the end of the feed
        </div>
      )}
    </div>
  );
};

export default SocialFeed;